import React from 'react'
import { FiArrowRight } from '@react-icons/all-files/fi/FiArrowRight'
import { FiCalendar } from '@react-icons/all-files/fi/FiCalendar'
import { FiCheck } from '@react-icons/all-files/fi/FiCheck'
import { FiClock } from '@react-icons/all-files/fi/FiClock'
import { FiGrid } from '@react-icons/all-files/fi/FiGrid'
import { FiLayers } from '@react-icons/all-files/fi/FiLayers'
import { FiMail } from '@react-icons/all-files/fi/FiMail'
import { FiMapPin } from '@react-icons/all-files/fi/FiMapPin'
import { FiMonitor } from '@react-icons/all-files/fi/FiMonitor'
import { FiPhone } from '@react-icons/all-files/fi/FiPhone'
import { FiSearch } from '@react-icons/all-files/fi/FiSearch'
import { FiSun } from '@react-icons/all-files/fi/FiSun'
import { FiTool } from '@react-icons/all-files/fi/FiTool'
import { FiUsers } from '@react-icons/all-files/fi/FiUsers'
import { FiVolume2 } from '@react-icons/all-files/fi/FiVolume2'
import * as gtag from '../../../lib/gtag'
import PageShell from '../foundations/PageShell'
import Container from '../foundations/Container'
import Action from '../primitives/Action'
import Media from '../primitives/Media'
import { MEDIA_POLICY_KEYS } from '../primitives/mediaPolicy'
import ServiceHero from './ServiceHero'
import ReviewProof from './ReviewProof'
import FAQGroup from './FAQGroup'
import CorporateProposalForm from './CorporateProposalForm'
import shared from './CorporateProductionPage.module.scss'
import styles from './TradeShowProductionPage.module.scss'

const capabilityIcons = {
  audio: FiVolume2,
  video: FiMonitor,
  lighting: FiSun,
  content: FiGrid,
  crew: FiTool,
  coordination: FiUsers
}

const formatIcons = [FiGrid, FiLayers, FiMonitor, FiUsers, FiVolume2, FiCalendar]

const SectionHeading = ({ eyebrow, title, description, inverse = false, id }) => (
  <div className={`${shared.sectionHeading} ${inverse ? shared.inverseHeading : ''}`.trim()}>
    {eyebrow && <span>{eyebrow}</span>}
    <h2 id={id}>{title}</h2>
    {description && <p>{description}</p>}
  </div>
)

const TradeShowProductionPage = ({ content, reviewData }) => {
  const track = (action, value = 3, sourceSurface) => gtag.event({
    action,
    label: 'leads',
    value,
    category: 'orders',
    sourceSurface
  })

  const openProposal = sourceSurface => {
    track('trade_show_proposal_open', 15, sourceSurface)
  }

  const processSteps = content.process.steps.map((step, index) => ({
    title: step.title,
    description: step.description,
    icon: [FiSearch, FiCalendar, FiTool, FiCheck][index]
  }))

  return (
    <PageShell className={shared.page}>
      <ServiceHero
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Corporate Event Production', href: '/corporate-event-production' },
          { label: 'Trade Show & Expo AV' }
        ]}
        eyebrow={content.hero.eyebrow}
        title={content.hero.title}
        description={content.hero.description}
        image={{ src: content.hero.image, position: content.hero.imagePosition }}
        imageAlt={content.hero.imageAlt}
        stackActionsOnMobile
        primaryAction={{
          label: content.hero.primaryAction,
          icon: FiMail,
          href: '#trade-show-proposal',
          focusTargetOnNavigate: true,
          onClick: () => openProposal('trade_show_hero'),
          channel: 'quote',
          conversionKey: 'trade-show-av-services.hero.proposal',
          salesPath: 'managed'
        }}
        secondaryAction={{
          label: content.hero.secondaryAction,
          icon: FiPhone,
          href: 'tel:+13059744411',
          external: true,
          onClick: () => track('trade_show_hero_call', 15),
          channel: 'phone',
          conversionKey: 'trade-show-av-services.hero.call',
          salesPath: 'managed'
        }}
        processLabel='Trade show production process'
        steps={processSteps}
      />

      <section className={shared.proof} aria-label='Trade show production proof'>
        <Container className={shared.proofGrid}>
          {content.proof.map(item => (
            <div key={item.key}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </Container>
      </section>

      <section className={shared.paths} aria-labelledby='trade-show-paths-title'>
        <Container>
          <SectionHeading id='trade-show-paths-title' eyebrow={content.paths.eyebrow} title={content.paths.title} description={content.paths.description} />
          <div className={shared.pathGrid}>
            <article className={`${shared.pathCard} ${shared.managedPath}`}>
              <span>{content.paths.managed.label}</span>
              <h3>{content.paths.managed.title}</h3>
              <p>{content.paths.managed.description}</p>
              <Action size='large' icon={FiArrowRight} href='#trade-show-proposal' focusTargetOnNavigate onClick={() => openProposal('trade_show_path_choice')}>
                {content.paths.managed.action}
              </Action>
            </article>
            <article className={shared.pathCard}>
              <span>{content.paths.equipment.label}</span>
              <h3>{content.paths.equipment.title}</h3>
              <p>{content.paths.equipment.description}</p>
              <Action variant='secondary' size='large' href='/led-video-wall-rental' icon={FiMonitor} onClick={() => track('trade_show_video_wall_path')}>
                {content.paths.equipment.action}
              </Action>
              <div className={styles.equipmentLinks} aria-label='Specific trade show equipment pages'>
                <Action variant='plain' href='/tv-rental' icon={FiArrowRight}>TV rentals</Action>
                <Action variant='plain' href='/projector-rental' icon={FiArrowRight}>Projector rentals</Action>
              </div>
            </article>
          </div>
        </Container>
      </section>

      <section className={shared.eventTypes} aria-labelledby='trade-show-formats-title'>
        <Container>
          <SectionHeading id='trade-show-formats-title' eyebrow={content.formats.eyebrow} title={content.formats.title} description={content.formats.description} />
          <div className={shared.eventTypeGrid}>
            {content.formats.items.map((item, index) => {
              const Icon = formatIcons[index % formatIcons.length]
              return (
                <article key={item.title}>
                  <Icon aria-hidden='true' />
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              )
            })}
          </div>
        </Container>
      </section>

      <section className={styles.gallery} aria-labelledby='trade-show-gallery-title'>
        <Container>
          <SectionHeading id='trade-show-gallery-title' eyebrow={content.gallery.eyebrow} title={content.gallery.title} description={content.gallery.description} />
          <div className={styles.galleryGrid}>
            {content.gallery.items.map((item, index) => (
              <article className={index === 0 || index === 5 ? styles.galleryFeature : ''} key={item.src}>
                <Media
                  src={item.src}
                  alt={item.alt}
                  ratio={index === 0 || index === 5 ? '16 / 10' : '4 / 3'}
                  policy={MEDIA_POLICY_KEYS.CARD}
                />
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className={shared.capabilities} aria-labelledby='trade-show-capabilities-title'>
        <Container>
          <SectionHeading id='trade-show-capabilities-title' inverse eyebrow={content.capabilities.eyebrow} title={content.capabilities.title} description={content.capabilities.description} />
          <div className={shared.capabilityGrid}>
            {content.capabilities.items.map(item => {
              const Icon = capabilityIcons[item.icon] || FiCheck
              return (
                <article key={item.title}>
                  <span><Icon aria-hidden='true' /></span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              )
            })}
          </div>
        </Container>
      </section>

      <section className={styles.coordination} aria-labelledby='trade-show-coordination-title'>
        <Container className={shared.venueLayout}>
          <div className={shared.venueCopy}>
            <SectionHeading id='trade-show-coordination-title' eyebrow={content.coordination.eyebrow} title={content.coordination.title} description={content.coordination.description} />
            <ul>
              {content.coordination.items.map(item => <li key={item}><FiCheck aria-hidden='true' /><span>{item}</span></li>)}
            </ul>
            <Action href='#trade-show-proposal' focusTargetOnNavigate onClick={() => openProposal('trade_show_coordination')} icon={FiArrowRight}>Discuss your exhibit</Action>
          </div>
          <Media src={content.coordination.image} alt={content.coordination.imageAlt} ratio='5 / 4' policy={MEDIA_POLICY_KEYS.SECTION_SPLIT} />
        </Container>
      </section>

      <section className={styles.process} aria-labelledby='trade-show-process-title'>
        <Container>
          <SectionHeading id='trade-show-process-title' eyebrow={content.process.eyebrow} title={content.process.title} description={content.process.description} />
          <ol className={styles.processGrid}>
            {content.process.steps.map((step, index) => (
              <li key={step.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </li>
            ))}
          </ol>
        </Container>
      </section>

      <ReviewProof
        eyebrow='Verified Google feedback'
        heading='Trusted for event production across Florida'
        pagePath='/trade-show-av-services'
        initialData={reviewData}
        sectionId='trade-show-production-reviews'
      />

      <FAQGroup
        questions={content.faqs}
        eyebrow='Exhibitor planning questions'
        title='Trade show AV services FAQs'
        intro='Clear answers about booth scope, show documents, equipment-only rentals, timelines, coordination, and Florida coverage.'
        headingId='trade-show-production-faqs'
      />

      <section id='trade-show-proposal' className={shared.proposal} aria-labelledby='trade-show-proposal-title'>
        <Container className={shared.proposalLayout}>
          <div className={shared.proposalIntro}>
            <SectionHeading id='trade-show-proposal-title' eyebrow={content.proposal.eyebrow} title={content.proposal.title} description={content.proposal.description} />
            <div className={shared.proposalNotes}>
              <span><FiClock aria-hidden='true' /> Share what you know now; show details can be advanced later.</span>
              <span><FiMapPin aria-hidden='true' /> Miami, Fort Lauderdale, Orlando, and Florida projects.</span>
              <span><FiUsers aria-hidden='true' /> Reviewed by a production team, not a generic rental queue.</span>
            </div>
          </div>
          <div className={shared.proposalFormCard}>
            <CorporateProposalForm idPrefix='trade-show-proposal-inline' mode='trade-show' />
          </div>
        </Container>
      </section>

      <section className={shared.related} aria-labelledby='trade-show-related-title'>
        <Container>
          <SectionHeading id='trade-show-related-title' eyebrow={content.related.eyebrow} title={content.related.title} />
          <div className={shared.relatedGrid}>
            {content.related.items.map(item => (
              <article key={item.href}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <Action variant='plain' href={item.href} icon={FiArrowRight}>Explore service</Action>
              </article>
            ))}
          </div>
        </Container>
      </section>
    </PageShell>
  )
}

export default TradeShowProductionPage
